/**
 * Block model baking: blockstate JSON (variants / multipart) -> model JSON with
 * parent inheritance -> list of quads in block space (0..1), rotated per
 * element and per variant, with texture layer, tint index and cull face.
 *
 * Implements the vanilla model format so modded blocks work as long as their
 * jar is supplied as an asset pack.
 */
import type { ParsedState } from "@mcp/world";
import { nsId, type AssetBundle } from "./pack.ts";

export const DIRS = ["down", "up", "north", "south", "west", "east"] as const;
export type DirName = (typeof DIRS)[number];
export const DIR_VEC: [number, number, number][] = [
  [0, -1, 0], [0, 1, 0], [0, 0, -1], [0, 0, 1], [-1, 0, 0], [1, 0, 0],
];

export interface BakedQuad {
  /** 4 corners x,y,z in block units (0..1) */
  pos: Float32Array;
  /** 4 corners u,v in 0..1 texture space */
  uv: Float32Array;
  tex: number;
  tint: number; // -1 none
  cull: number; // -1 none, else direction index
  /** direction if the quad lies flat on the block boundary (used for AO), else -1 */
  face: number;
  /** vanilla directional shading factor */
  shade: number;
  /** texture alpha class 0/1/2 */
  alpha: number;
}

export type TintKind = "none" | "grass" | "foliage" | "dry_foliage" | "water" | "fixed" | "redstone" | "stem";

export interface BakedModel {
  quads: BakedQuad[];
  /** occludes neighbours completely (full opaque cube) */
  fullOpaque: boolean;
  /** full cube shape (for same-block culling of glass/leaves/ice) */
  fullCube: boolean;
  ao: boolean;
  fluid: null | { kind: "water" | "lava"; level: number };
  tint: TintKind;
  tintFixed: number; // 0xRRGGBB for fixed / redstone power
  /** Map colour of the top face (untinted avg) or null if the block has no top face (plants, torches…) */
  top: null | { r: number; g: number; b: number; tinted: boolean; translucent: boolean };
  translucent: boolean;
  unknown: boolean;
}

interface ModelJson {
  parent?: string;
  textures?: Record<string, string>;
  elements?: ElementJson[];
  ambientocclusion?: boolean;
}
interface ElementJson {
  from: number[];
  to: number[];
  rotation?: { origin: number[]; axis: "x" | "y" | "z"; angle: number; rescale?: boolean };
  shade?: boolean;
  faces: Partial<Record<DirName, { uv?: number[]; texture: string; cullface?: DirName | "bottom"; rotation?: number; tintindex?: number }>>;
}
interface VariantRef { model: string; x?: number; y?: number; z?: number; uvlock?: boolean; weight?: number }

const IMPLICIT_WATER = new Set(["minecraft:kelp", "minecraft:kelp_plant", "minecraft:seagrass", "minecraft:tall_seagrass", "minecraft:bubble_column"]);

export class ModelBaker {
  private texIndex = new Map<string, number>();
  private resolved = new Map<string, { textures: Record<string, string>; elements: ElementJson[]; ao: boolean } | null>();
  private cache = new Map<string, BakedModel>();
  readonly bundle: Pick<AssetBundle, "blockstates" | "models" | "textures">;

  constructor(bundle: Pick<AssetBundle, "blockstates" | "models" | "textures">) {
    this.bundle = bundle;
    bundle.textures.names.forEach((n, i) => this.texIndex.set(n, i));
  }

  textureLayer(name: string) {
    return this.texIndex.get(nsId(name)) ?? 0;
  }

  bake(key: string, st: ParsedState): BakedModel {
    let m = this.cache.get(key);
    if (!m) {
      m = this.bakeUncached(st);
      this.cache.set(key, m);
    }
    return m;
  }

  private bakeUncached(st: ParsedState): BakedModel {
    const name = st.name;
    const quads: BakedQuad[] = [];
    let ao = true;
    let unknown = false;
    const bs = this.bundle.blockstates[name] as { variants?: Record<string, VariantRef | VariantRef[]>; multipart?: { when?: unknown; apply: VariantRef | VariantRef[] }[] } | undefined;
    const refs: VariantRef[] = [];
    if (!bs) unknown = name !== "minecraft:air" && name !== "minecraft:cave_air" && name !== "minecraft:void_air";
    else if (bs.variants) {
      const keys = Object.keys(bs.variants);
      const key = keys.find((k) => variantMatches(k, st.props)) ?? defaultVariant(keys, st.props);
      if (key !== undefined) { const v = bs.variants[key]; refs.push(Array.isArray(v) ? v[0] : v); }
    } else if (bs.multipart) {
      for (const part of bs.multipart) {
        if (!part.when || whenMatches(part.when, st.props)) refs.push(Array.isArray(part.apply) ? part.apply[0] : part.apply);
      }
    }
    let particle: string | null = null;
    for (const ref of refs) {
      const r = this.resolveModel(nsId(ref.model));
      if (!r) continue;
      if (!r.ao) ao = false;
      particle ??= resolveTex(r.textures, "#particle");
      for (const el of r.elements) this.bakeElement(el, r.textures, ref, quads);
    }

    // Entity-rendered blocks have no elements: give them a simple stand-in box so they show up in plans.
    if (!quads.length && particle && !isInvisible(name)) {
      const box = fallbackBox(name);
      if (box) {
        const el: ElementJson = { from: box[0], to: box[1], faces: {} };
        for (const d of DIRS) el.faces[d] = { texture: "#p", cullface: undefined };
        this.bakeElement(el, { p: particle }, { model: "" }, quads);
      }
    }
    if (unknown) {
      // magenta-ish placeholder cube for blocks without assets (missing mod jar)
      const el: ElementJson = { from: [0, 0, 0], to: [16, 16, 16], faces: {} };
      for (const d of DIRS) el.faces[d] = { texture: "#missing", cullface: d };
      this.bakeElement(el, { missing: "missing" }, { model: "" }, quads);
    }

    // occlusion / cube classification
    let fullOpaque = quads.length > 0;
    let fullCube = quads.length > 0;
    for (let d = 0; d < 6; d++) {
      const full = quads.filter((q) => q.cull === d && isFullFace(q, d));
      if (!full.length) { fullOpaque = fullCube = false; break; }
      if (!full.some((q) => q.alpha === 0)) fullOpaque = false;
    }
    if (quads.some((q) => q.cull < 0) && fullCube) {
      // extra inner geometry (e.g. slime, honey) -> not a simple cube
      if (quads.some((q) => q.cull < 0 && q.alpha === 2)) fullOpaque = false;
    }

    // fluids
    let fluid: BakedModel["fluid"] = null;
    if (name === "minecraft:water" || name === "minecraft:lava") {
      fluid = { kind: name === "minecraft:water" ? "water" : "lava", level: +(st.props.level ?? 0) };
      fullOpaque = fullCube = false;
    } else if (st.props.waterlogged === "true" || IMPLICIT_WATER.has(name)) {
      fluid = { kind: "water", level: 0 };
    }

    const { tint, fixed } = tintFor(name, st.props);
    const translucent = quads.some((q) => q.alpha === 2) || fluid?.kind === "water";

    // map colour
    let top: BakedModel["top"] = null;
    let r = 0, g = 0, b = 0, w = 0, tinted = 0, trans = 0;
    const avg = this.bundle.textures.avg;
    for (const q of quads) {
      const ny = normalY(q);
      if (ny < 0.7) continue;
      const area = quadArea(q);
      const hy = Math.max(q.pos[1], q.pos[4], q.pos[7], q.pos[10]);
      const wgt = area * (0.25 + hy);
      r += avg[q.tex * 3] * wgt; g += avg[q.tex * 3 + 1] * wgt; b += avg[q.tex * 3 + 2] * wgt; w += wgt;
      if (q.tint >= 0) tinted += wgt;
      if (q.alpha === 2) trans += wgt;
    }
    if (w > 0.2) top = { r: r / w, g: g / w, b: b / w, tinted: tinted > w / 2, translucent: trans > w / 2 };
    if (fluid && fluid.kind === "water" && !top) top = { r: 180, g: 180, b: 180, tinted: true, translucent: true };
    if (fluid && fluid.kind === "lava") top = { r: 207, g: 92, b: 20, tinted: false, translucent: false };
    if (fluid?.kind === "water" && name === "minecraft:water") top = { r: 180, g: 180, b: 180, tinted: true, translucent: true };

    return { quads, fullOpaque, fullCube, ao, fluid, tint, tintFixed: fixed, top, translucent, unknown };
  }

  private resolveModel(id: string) {
    if (this.resolved.has(id)) return this.resolved.get(id)!;
    const chain: ModelJson[] = [];
    let cur: string | undefined = id;
    for (let i = 0; cur && i < 16; i++) {
      const m = this.bundle.models[nsId(cur)] as ModelJson | undefined;
      if (!m) break;
      chain.push(m);
      cur = m.parent;
    }
    if (!chain.length) { this.resolved.set(id, null); return null; }
    const textures: Record<string, string> = {};
    let elements: ElementJson[] | undefined;
    let ao = true;
    for (let i = chain.length - 1; i >= 0; i--) Object.assign(textures, chain[i].textures ?? {});
    for (const m of chain) if (m.elements) { elements = m.elements; break; }
    for (const m of chain) if (m.ambientocclusion !== undefined) { ao = m.ambientocclusion; break; }
    const r = { textures, elements: elements ?? [], ao };
    this.resolved.set(id, r);
    return r;
  }

  private bakeElement(el: ElementJson, textures: Record<string, string>, ref: VariantRef, out: BakedQuad[]) {
    const [x0, y0, z0] = el.from;
    const [x1, y1, z1] = el.to;
    for (let d = 0; d < 6; d++) {
      const f = el.faces[DIRS[d]];
      if (!f) continue;
      const texName = resolveTex(textures, f.texture);
      const tex = texName ? this.textureLayer(texName) : 0;
      // corners in pixel space per face (order matches uv corners (u1,v1),(u1,v2),(u2,v2),(u2,v1))
      const c = faceCorners(d, x0, y0, z0, x1, y1, z1);
      const uvr = f.uv ?? defaultUv(d, x0, y0, z0, x1, y1, z1);
      const uc = [[uvr[0], uvr[1]], [uvr[0], uvr[3]], [uvr[2], uvr[3]], [uvr[2], uvr[1]]];
      const rot = (((f.rotation ?? 0) / 90) | 0) & 3;
      const pos = new Float32Array(12);
      const uv = new Float32Array(8);
      for (let i = 0; i < 4; i++) {
        let p = c[i];
        if (el.rotation) p = rotateElement(p, el.rotation);
        p = rotateModel(p, ref.x ?? 0, ref.y ?? 0);
        pos[i * 3] = p[0] / 16; pos[i * 3 + 1] = p[1] / 16; pos[i * 3 + 2] = p[2] / 16;
        const u = uc[(i + rot) & 3];
        uv[i * 2] = u[0] / 16; uv[i * 2 + 1] = u[1] / 16;
      }
      let cull = -1;
      if (f.cullface) {
        const cd = f.cullface === "bottom" ? 0 : DIRS.indexOf(f.cullface as DirName);
        if (cd >= 0) cull = rotateDir(cd, ref.x ?? 0, ref.y ?? 0);
      }
      const n = normal(pos);
      const shade = el.shade === false ? 1 : Math.min(1, n[0] * n[0] * 0.6 + n[1] * n[1] * (n[1] > 0 ? 1 : 0.5) + n[2] * n[2] * 0.8);
      out.push({ pos, uv, tex, tint: f.tintindex ?? -1, cull, face: boundaryFace(pos, n), shade, alpha: this.bundle.textures.alpha[tex] });
    }
  }
}

// ---------- helpers ----------

function resolveTex(t: Record<string, string>, ref: string): string | null {
  let v: string | undefined = ref;
  for (let i = 0; i < 12 && v && v.startsWith("#"); i++) v = t[v.slice(1)];
  if (!v || v.startsWith("#")) return null;
  return v === "missing" ? "missing" : nsId(v);
}

/** Values a block's default state most likely has (the game code knows; the assets don't). Earlier = likelier. */
const LIKELY_DEFAULT: Record<string, string[]> = {
  axis: ["y"], facing: ["north", "down"], half: ["bottom", "lower"], type: ["bottom", "single"], shape: ["straight", "north_south"],
  face: ["wall"], hinge: ["left"], attachment: ["floor"], part: ["foot"], orientation: ["north_up"], thickness: ["tip"],
  vertical_direction: ["up"], tilt: ["none"], leaves: ["none"], mode: ["save", "compare"], sculk_sensor_phase: ["inactive"],
};

function defaultScore(k: string, v: string) {
  const d = LIKELY_DEFAULT[k];
  if (d) { const i = d.indexOf(v); return i < 0 ? 0 : 3 - i; }
  if (v === "false" || v === "none") return 2;
  if (/^\d+$/.test(v)) return 1 / (1 + Number(v)); // age=0, level=0, …
  return 0;
}

/**
 * From 26.3 a world writes a block's default state as its bare name, without properties ("minecraft:oak_log" is
 * axis=y). Pick the variant that most looks like the default: the properties the state has must match, the missing
 * ones are scored with LIKELY_DEFAULT.
 */
export function defaultVariant(keys: string[], props: Record<string, string>): string | undefined {
  let best: string | undefined, bestScore = -Infinity;
  for (const key of keys) {
    let score = 0, ok = true;
    for (const kv of key.split(",")) {
      const [k, v = ""] = kv.split("=");
      if (k in props) { if (props[k] !== v) { ok = false; break; } continue; }
      score += defaultScore(k, v);
    }
    if (ok && score > bestScore) { best = key; bestScore = score; }
  }
  return best;
}

/** A property a 26.3 default state leaves out (multipart blocks: fences, walls, redstone …). */
function guessDefault(k: string, opts: string[]) {
  if (k === "up") return "true"; // walls and mushroom blocks
  const d = LIKELY_DEFAULT[k];
  if (d) return d[0];
  if (opts.some((o) => /^\d+$/.test(o))) return "0";
  if (opts.some((o) => o === "none" || o === "low" || o === "tall" || o === "side" || o === "up")) return "none";
  return "false";
}

function variantMatches(key: string, props: Record<string, string>) {
  if (key === "" || key === "normal") return true;
  for (const kv of key.split(",")) {
    const [k, v] = kv.split("=");
    if ((props[k] ?? "") !== v) return false;
  }
  return true;
}

function whenMatches(when: unknown, props: Record<string, string>): boolean {
  const w = when as Record<string, unknown>;
  if (Array.isArray(w.OR)) return (w.OR as unknown[]).some((c) => whenMatches(c, props));
  if (Array.isArray(w.AND)) return (w.AND as unknown[]).every((c) => whenMatches(c, props));
  for (const [k, v] of Object.entries(w)) {
    let s = String(v);
    let neg = false;
    if (s.startsWith("!")) { neg = true; s = s.slice(1); }
    const opts = s.split("|");
    const ok = opts.includes(props[k] ?? guessDefault(k, opts));
    if (ok === neg) return false;
  }
  return true;
}

function faceCorners(d: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): number[][] {
  switch (d) {
    case 0: return [[x0, y0, z1], [x0, y0, z0], [x1, y0, z0], [x1, y0, z1]];
    case 1: return [[x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0]];
    case 2: return [[x1, y1, z0], [x1, y0, z0], [x0, y0, z0], [x0, y1, z0]];
    case 3: return [[x0, y1, z1], [x0, y0, z1], [x1, y0, z1], [x1, y1, z1]];
    case 4: return [[x0, y1, z0], [x0, y0, z0], [x0, y0, z1], [x0, y1, z1]];
    default: return [[x1, y1, z1], [x1, y0, z1], [x1, y0, z0], [x1, y1, z0]];
  }
}

function defaultUv(d: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): number[] {
  switch (d) {
    case 0: return [x0, 16 - z1, x1, 16 - z0];
    case 1: return [x0, z0, x1, z1];
    case 2: return [16 - x1, 16 - y1, 16 - x0, 16 - y0];
    case 3: return [x0, 16 - y1, x1, 16 - y0];
    case 4: return [z0, 16 - y1, z1, 16 - y0];
    default: return [16 - z1, 16 - y1, 16 - z0, 16 - y0];
  }
}

function rotateElement(p: number[], r: NonNullable<ElementJson["rotation"]>): number[] {
  const a = (r.angle * Math.PI) / 180;
  const c = Math.cos(a), s = Math.sin(a);
  const [ox, oy, oz] = r.origin;
  let x = p[0] - ox, y = p[1] - oy, z = p[2] - oz;
  const k = r.rescale ? 1 / Math.max(Math.abs(c), 1e-3) : 1;
  if (r.axis === "y") { const nx = x * c + z * s, nz = -x * s + z * c; x = nx * k; z = nz * k; }
  else if (r.axis === "x") { const ny = y * c - z * s, nz = y * s + z * c; y = ny * k; z = nz * k; }
  else { const nx = x * c - y * s, ny = x * s + y * c; x = nx * k; y = ny * k; }
  return [x + ox, y + oy, z + oz];
}

/** Variant rotation in 90° steps: x first, then y (vanilla order). */
function rotateModel(p: number[], rx: number, ry: number): number[] {
  let [x, y, z] = p;
  for (let i = 0; i < (((rx / 90) | 0) & 3); i++) { const ny = z, nz = 16 - y; y = ny; z = nz; }
  for (let i = 0; i < (((ry / 90) | 0) & 3); i++) { const nx = 16 - z, nz = x; x = nx; z = nz; }
  return [x, y, z];
}

function rotateDir(d: number, rx: number, ry: number): number {
  const v = DIR_VEC[d];
  const p = rotateModel([8 + v[0], 8 + v[1], 8 + v[2]], rx, ry);
  const r = [Math.round(p[0] - 8), Math.round(p[1] - 8), Math.round(p[2] - 8)];
  return DIR_VEC.findIndex((q) => q[0] === r[0] && q[1] === r[1] && q[2] === r[2]);
}

function normal(pos: Float32Array): [number, number, number] {
  const ax = pos[3] - pos[0], ay = pos[4] - pos[1], az = pos[5] - pos[2];
  const bx = pos[6] - pos[0], by = pos[7] - pos[1], bz = pos[8] - pos[2];
  let nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
  const l = Math.hypot(nx, ny, nz) || 1;
  nx /= l; ny /= l; nz /= l;
  return [nx, ny, nz];
}
function normalY(q: BakedQuad) {
  return normal(q.pos)[1];
}
function quadArea(q: BakedQuad) {
  const p = q.pos;
  const ax = p[3] - p[0], ay = p[4] - p[1], az = p[5] - p[2];
  const bx = p[9] - p[0], by = p[10] - p[1], bz = p[11] - p[2];
  return Math.hypot(ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx);
}

function boundaryFace(pos: Float32Array, n: [number, number, number]): number {
  const eps = 1e-4;
  for (let d = 0; d < 6; d++) {
    const v = DIR_VEC[d];
    if (n[0] * v[0] + n[1] * v[1] + n[2] * v[2] < 0.999) continue;
    const axis = v[0] ? 0 : v[1] ? 1 : 2;
    const target = v[axis] > 0 ? 1 : 0;
    if (Math.abs(pos[axis] - target) < eps && Math.abs(pos[3 + axis] - target) < eps && Math.abs(pos[6 + axis] - target) < eps) return d;
  }
  return -1;
}

function isFullFace(q: BakedQuad, d: number) {
  if (q.face !== d) return false;
  return Math.abs(quadArea(q) - 1) < 1e-3;
}

function isInvisible(name: string) {
  return /(^|:)(air|cave_air|void_air|barrier|light|structure_void|moving_piston)$/.test(name) || /_sign$|_banner$|_wall_banner$|conduit$/.test(name);
}

function fallbackBox(name: string): [number[], number[]] | null {
  if (/chest$/.test(name)) return [[1, 0, 1], [15, 14, 15]];
  if (/_bed$/.test(name)) return [[0, 0, 0], [16, 9, 16]];
  if (/shulker_box$/.test(name)) return [[0, 0, 0], [16, 16, 16]];
  if (/(_head|_skull)$/.test(name)) return [[4, 0, 4], [12, 8, 12]];
  if (/decorated_pot$/.test(name)) return [[1, 0, 1], [15, 16, 15]];
  if (/copper_golem_statue$/.test(name)) return [[4, 0, 4], [12, 12, 12]];
  return null;
}

const FOLIAGE = new Set(["oak_leaves", "jungle_leaves", "acacia_leaves", "dark_oak_leaves", "mangrove_leaves", "vine"]);
const GRASS = new Set(["grass_block", "short_grass", "tall_grass", "fern", "large_fern", "potted_fern", "sugar_cane", "bush", "pink_petals", "wildflowers"]);

function tintFor(name: string, props: Record<string, string>): { tint: TintKind; fixed: number } {
  const [ns, n] = name.split(":");
  if (ns === "minecraft") {
    if (n === "birch_leaves") return { tint: "fixed", fixed: 0x80a755 };
    if (n === "spruce_leaves") return { tint: "fixed", fixed: 0x619961 };
    if (n === "lily_pad") return { tint: "fixed", fixed: 0x208030 };
    if (n === "leaf_litter") return { tint: "dry_foliage", fixed: 0 };
    if (FOLIAGE.has(n)) return { tint: "foliage", fixed: 0 };
    if (GRASS.has(n)) return { tint: "grass", fixed: 0 };
    if (n === "water" || n === "water_cauldron" || n === "bubble_column") return { tint: "water", fixed: 0 };
    if (n === "redstone_wire") {
      const p = +(props.power ?? 0) / 15;
      const r = p * 0.6 + (p > 0 ? 0.4 : 0.3);
      const g = Math.max(0, p * p * 0.7 - 0.5), b = Math.max(0, p * p * 0.6 - 0.7);
      return { tint: "redstone", fixed: (Math.round(r * 255) << 16) | (Math.round(g * 255) << 8) | Math.round(b * 255) };
    }
    if (/stem$/.test(n)) {
      const age = +(props.age ?? 7);
      return { tint: "stem", fixed: n.startsWith("attached") ? 0xe0c71c : ((age * 32) << 16) | ((255 - age * 8) << 8) | (age * 4) };
    }
  } else if (/leaves$/.test(n)) return { tint: "foliage", fixed: 0 };
  return { tint: "grass", fixed: 0 };
}
